/**
 * QuillStudio — newsroom script desk for live production drafting.
 *
 * Visual identity: control-room panels, active rundown orchestration,
 * and real-time scripting telemetry. Writing logic and backend behavior
 * remain unchanged.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';

const QUILL_YELLOW = '#ffb86c';
const QUILL_CREAM = '#e6eef8';
const QUILL_ESPRESSO = '#04070f';
const QUILL_CHAR = '#0b1320';
const QUILL_INK = '#d9e7f7';
const QUILL_MUTED = 'rgba(188, 205, 229, 0.62)';
const QUILL_LINE = 'rgba(255, 122, 69, 0.24)';

const VIEWS = [
  { id: 'library',    label: 'Wire Desk',       blurb: 'Live source intake — reference feeds and verification links' },
  { id: 'reading',    label: 'Ingest Queue',    blurb: 'Items currently being parsed into script-ready context' },
  { id: 'notes',      label: 'Pitch Notes',     blurb: 'Angles, tension notes, and conflict probes' },
  { id: 'desk',       label: 'Script Desk',     blurb: 'Active drafts, revisions, and anchor handoffs' },
  { id: 'story',      label: 'Segment Lab',     blurb: 'Segment framing, pacing, and sequence architecture' },
  { id: 'script',     label: 'Rundown Blocks',  blurb: 'Openers, transitions, lower-thirds, and closures' },
  { id: 'interview',  label: 'Interview Queue', blurb: 'Probe sequence for guest and analyst exchanges' },
  { id: 'archive',    label: 'Run Archive',     blurb: 'Completed scripts and prior episode packets' },
];

const MODES = [
  { id: 'episode_opener',    label: 'Episode Opener'    },
  { id: 'monologue',         label: 'Monologue'         },
  { id: 'segment_script',    label: 'Segment Script'    },
  { id: 'interview_questions', label: 'Interview Questions' },
  { id: 'story',             label: 'Story'             },
  { id: 'scene_dialogue',    label: 'Scene Dialogue'    },
  { id: 'character_voice',   label: 'Character Voice'   },
  { id: 'transitions',       label: 'Transitions'       },
  { id: 'short_form_social', label: 'Short-form Social' },
  { id: 'narration',         label: 'Narration'         },
];

// --------------- UI building blocks ----------------

function Shelf({ title, children, right = null, testId }) {
  return (
    <section data-testid={testId} style={{
      background: QUILL_CHAR,
      border: `1px solid ${QUILL_LINE}`,
      borderRadius: 4,
      padding: '18px 20px',
      marginBottom: 18,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        borderBottom: `1px solid ${QUILL_LINE}`, paddingBottom: 8, marginBottom: 14,
      }}>
        <h3 style={{
          fontFamily: 'Georgia, "Iowan Old Style", "Times New Roman", serif',
          color: QUILL_YELLOW, margin: 0, fontSize: 18, letterSpacing: '0.02em',
        }}>{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function PrimaryBtn({ children, onClick, disabled, testId, style = {} }) {
  return (
    <button
      onClick={onClick} disabled={disabled} data-testid={testId}
      style={{
        background: disabled ? 'rgba(212,165,116,0.15)' : QUILL_YELLOW,
        color: disabled ? QUILL_MUTED : QUILL_ESPRESSO,
        border: 'none', padding: '8px 16px',
        fontFamily: 'Georgia, serif', fontSize: 13, letterSpacing: '0.04em',
        cursor: disabled ? 'progress' : 'pointer', borderRadius: 2,
        transition: 'background 120ms',
        ...style,
      }}
    >{children}</button>
  );
}

function GhostBtn({ children, onClick, testId, active = false, disabled = false, style = {} }) {
  return (
    <button
      onClick={onClick} data-testid={testId} disabled={disabled}
      style={{
        background: active ? 'rgba(212,165,116,0.12)' : 'transparent',
        color: disabled ? QUILL_MUTED : (active ? QUILL_YELLOW : QUILL_INK),
        border: `1px solid ${active ? QUILL_YELLOW : QUILL_LINE}`,
        padding: '6px 12px', fontSize: 12, letterSpacing: '0.06em',
        fontFamily: 'Georgia, serif', cursor: disabled ? 'progress' : 'pointer', borderRadius: 2,
        opacity: disabled ? 0.55 : 1,
        ...style,
      }}
    >{children}</button>
  );
}

// --------------- Wire Desk view ----------------

function LibraryView() {
  const [q, setQ] = useState('grief');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const runSearch = useCallback(async (query) => {
    if (!query.trim()) return;
    setLoading(true); setErr(null);
    try {
      const r = await api.post('/api/quill/retrieval/search', { q: query, limit: 5, save: true });
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'search failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { runSearch(q); /* initial search only */ }, []);

  const grouped = useMemo(() => {
    const out = { wikipedia: [], open_library: [], gutenberg: [], standard_ebooks: [] };
    items.forEach((i) => { (out[i.provider] = out[i.provider] || []).push(i); });
    return out;
  }, [items]);

  const PROVIDERS = [
    { id: 'wikipedia',       label: 'Wire Reference',     blurb: 'context index' },
    { id: 'open_library',    label: 'Archive Metadata',   blurb: 'catalog records' },
    { id: 'gutenberg',       label: 'Public Source Feed', blurb: 'open corpus' },
    { id: 'standard_ebooks', label: 'Curated Feed',       blurb: 'edited corpus' },
  ];

  return (
    <div data-testid="quill-library-view">
      <Shelf title="Wire Desk · Source Discovery" testId="quill-library-shelf" right={
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch(q)}
            placeholder="Search topic, author, or title…"
            data-testid="quill-library-search"
            style={{
              background: QUILL_ESPRESSO, color: QUILL_INK,
              border: `1px solid ${QUILL_LINE}`, padding: '6px 10px',
              fontFamily: 'Georgia, serif', fontSize: 13, borderRadius: 2,
              minWidth: 260,
            }}
          />
          <PrimaryBtn onClick={() => runSearch(q)} disabled={loading} testId="quill-library-go">
            {loading ? 'READING…' : 'SEARCH'}
          </PrimaryBtn>
        </div>
      }>
        {err && <div style={{ color: '#f87171', fontSize: 12 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
          {PROVIDERS.map((p) => (
            <div key={p.id} data-testid={`quill-provider-${p.id}`} style={{
              background: QUILL_ESPRESSO, border: `1px solid ${QUILL_LINE}`,
              padding: '12px 14px', borderRadius: 3, minHeight: 180,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong style={{ color: QUILL_YELLOW, fontFamily: 'Georgia, serif', fontSize: 14 }}>
                  {p.label}
                </strong>
                <em style={{ color: QUILL_MUTED, fontSize: 10, letterSpacing: '0.1em' }}>
                  {p.blurb}
                </em>
              </div>
              {(grouped[p.id] || []).length === 0 ? (
                <div style={{ color: QUILL_MUTED, fontSize: 12 }}>No results yet.</div>
              ) : (
                (grouped[p.id] || []).map((row) => (
                  <a
                    key={row.source_id}
                    href={row.url || '#'} target="_blank" rel="noreferrer"
                    data-testid={`quill-source-${row.source_id}`}
                    style={{
                      display: 'block', padding: '6px 0',
                      borderBottom: '1px dashed rgba(212,165,116,0.12)',
                      color: QUILL_INK, textDecoration: 'none',
                    }}
                  >
                    <div style={{ fontFamily: 'Georgia, serif', fontSize: 13 }}>{row.title}</div>
                    <div style={{ color: QUILL_MUTED, fontSize: 11 }}>
                      {row.author ? `${row.author} · ` : ''}{row.tier.replace(/_/g, ' ')}
                    </div>
                  </a>
                ))
              )}
            </div>
          ))}
        </div>
      </Shelf>
    </div>
  );
}

// --------------- Script Desk view (LLM-powered) ----------------

function DraftDeskView({ onJumpToScript, onDraftStateChange }) {
  const [briefMode, setBriefMode] = useState('episode_opener');
  const [objective, setObjective] = useState('');
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('serious');
  const [hostVoice, setHostVoice] = useState('nova');
  const [brief, setBrief] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [outline, setOutline] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);   // 'brief' | 'outline' | 'draft' | 'revise'
  const [err, setErr] = useState(null);

  const reset = () => { setBrief(null); setQuestions([]); setOutline(null); setDraft(null); setErr(null); };

  const submitBrief = async () => {
    if (!objective.trim()) { setErr('Objective is required'); return; }
    setBusy('brief'); setErr(null);
    try {
      const r = await api.post('/api/quill/briefs', {
        mode: briefMode, objective, topic: topic || null,
        tone, host_voice: hostVoice, audience: 'general broadcast audience',
      });
      setBrief(r.data.brief);
      setQuestions(r.data.questions || []);
    } catch (e) { setErr(e?.response?.data?.detail || e?.message); }
    finally { setBusy(null); }
  };

  const applyAnswer = async (field, value) => {
    // Re-submit the brief with the new value. Simpler than PATCH for MVP.
    setBusy('brief');
    try {
      const payload = {
        mode: brief.mode, objective: brief.objective, topic: brief.topic,
        tone: brief.tone, host_voice: brief.host_voice, audience: brief.audience,
        [field]: value,
      };
      const r = await api.post('/api/quill/briefs', payload);
      setBrief(r.data.brief); setQuestions(r.data.questions || []);
    } finally { setBusy(null); }
  };

  const buildOutline = async () => {
    setBusy('outline'); setErr(null);
    try {
      const r = await api.post(`/api/quill/briefs/${brief.brief_id}/outline`);
      setOutline(r.data.outline);
    } catch (e) { setErr(e?.response?.data?.detail || e?.message); }
    finally { setBusy(null); }
  };

  const buildDraft = async () => {
    setBusy('draft'); setErr(null);
    try {
      const r = await api.post(`/api/quill/briefs/${brief.brief_id}/draft`, {
        outline_id: outline.outline_id,
      });
      setDraft(r.data.draft);
    } catch (e) { setErr(e?.response?.data?.detail || e?.message); }
    finally { setBusy(null); }
  };

  const revise = async (kind) => {
    setBusy('revise'); setErr(null);
    try {
      const r = await api.post(`/api/quill/drafts/${draft.draft_id}/revise`, { pass_kind: kind });
      setDraft(r.data.draft);
    } catch (e) { setErr(e?.response?.data?.detail || e?.message); }
    finally { setBusy(null); }
  };

  useEffect(() => {
    if (!onDraftStateChange) return;
    const words = draft?.text ? draft.text.split(/\s+/).filter(Boolean).length : 0;
    onDraftStateChange({
      mode: briefMode,
      topic: topic || brief?.topic || '',
      objective: objective || brief?.objective || '',
      hostVoice,
      busy: Boolean(busy),
      busyState: busy || 'idle',
      revision: draft?.revision || 0,
      wordCount: words,
      draftState: draft ? 'active' : brief ? 'queued' : 'idle',
      cues: outline?.beats?.slice(0, 4) || [],
    });
  }, [brief, briefMode, busy, draft, hostVoice, objective, onDraftStateChange, outline, topic]);

  return (
    <div data-testid="quill-desk-view">
      <Shelf title="Script Desk · Brief → Outline → Draft → Revise" testId="quill-desk-shelf" right={
        brief && <GhostBtn onClick={reset} testId="quill-reset-brief">NEW BRIEF</GhostBtn>
      }>
        {err && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 10 }}>{err}</div>}

        {/* Step 1: Brief */}
        {!brief && (
          <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}>
            <div>
              <label style={{ color: QUILL_MUTED, fontSize: 11, letterSpacing: '0.12em' }}>MODE</label>
              <select
                value={briefMode} onChange={(e) => setBriefMode(e.target.value)}
                data-testid="quill-mode-select"
                style={_input}
              >
                {MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={_label}>OBJECTIVE · required</label>
              <textarea
                value={objective} onChange={(e) => setObjective(e.target.value)}
                rows={2} data-testid="quill-objective"
                placeholder="e.g., Plant the idea that the Atlantic storm season has entered a new regime"
                style={{ ..._input, minHeight: 60, resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={_label}>TOPIC</label>
                <input value={topic} onChange={(e) => setTopic(e.target.value)} style={_input} data-testid="quill-topic" />
              </div>
              <div>
                <label style={_label}>TONE</label>
                <select value={tone} onChange={(e) => setTone(e.target.value)} style={_input} data-testid="quill-tone">
                  {['serious','playful','dramatic','intimate','reflective','persuasive'].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={_label}>HOST VOICE</label>
                <select value={hostVoice} onChange={(e) => setHostVoice(e.target.value)} style={_input} data-testid="quill-host">
                  {['nova','orion','atlas','vega','quill','neutral'].map((h) => <option key={h}>{h}</option>)}
                </select>
              </div>
            </div>
            <div>
              <PrimaryBtn onClick={submitBrief} disabled={busy === 'brief'} testId="quill-submit-brief">
                {busy === 'brief' ? 'THINKING…' : 'SUBMIT BRIEF'}
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* Step 2: Clarifying questions */}
        {brief && questions.length > 0 && (
          <div data-testid="quill-questions-panel" style={{ borderTop: `1px solid ${QUILL_LINE}`, paddingTop: 12, marginTop: 4 }}>
            <div style={{ color: QUILL_YELLOW, fontSize: 13, fontFamily: 'Georgia, serif', marginBottom: 10, fontStyle: 'italic' }}>
              Before I write, I have {questions.length} question{questions.length > 1 ? 's' : ''}:
            </div>
            {questions.map((q) => (
              <div key={q.field} data-testid={`quill-question-${q.field}`} style={{ marginBottom: 14 }}>
                <div style={{ color: QUILL_INK, fontFamily: 'Georgia, serif', fontSize: 14 }}>{q.question}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {(q.suggested_answers || []).map((s) => (
                    <GhostBtn key={s} onClick={() => applyAnswer(q.field, s)} testId={`quill-answer-${q.field}-${s}`}>
                      {s}
                    </GhostBtn>
                  ))}
                  {(q.suggested_answers || []).length === 0 && (
                    <input
                      placeholder="type your answer + Enter…"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                          applyAnswer(q.field, e.currentTarget.value.trim());
                        }
                      }}
                      style={{ ..._input, minWidth: 320 }}
                      data-testid={`quill-answer-input-${q.field}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 3: Outline */}
        {brief && questions.length === 0 && !outline && (
          <div data-testid="quill-outline-gate" style={{ borderTop: `1px solid ${QUILL_LINE}`, paddingTop: 12 }}>
            <div style={{ color: QUILL_YELLOW, fontSize: 14, marginBottom: 10, fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
              Brief is complete. Ready for an outline.
            </div>
            <PrimaryBtn onClick={buildOutline} disabled={busy === 'outline'} testId="quill-build-outline">
              {busy === 'outline' ? 'OUTLINING…' : 'BUILD OUTLINE'}
            </PrimaryBtn>
          </div>
        )}

        {outline && (
          <div data-testid="quill-outline-panel" style={{ borderTop: `1px solid ${QUILL_LINE}`, paddingTop: 12, marginTop: 10 }}>
            <div style={{ color: QUILL_YELLOW, fontFamily: 'Georgia, serif', fontSize: 15, marginBottom: 8 }}>
              Outline — “{outline.title}”
            </div>
            <ol data-testid="quill-beats" style={{ color: QUILL_INK, fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.55, paddingLeft: 20 }}>
              {outline.beats.map((b, i) => <li key={i}>{b}</li>)}
            </ol>
            {!draft && (
              <PrimaryBtn onClick={buildDraft} disabled={busy === 'draft'} testId="quill-build-draft">
                {busy === 'draft' ? 'DRAFTING…' : 'WRITE DRAFT'}
              </PrimaryBtn>
            )}
          </div>
        )}

        {draft && (
          <div data-testid="quill-draft-panel" style={{ borderTop: `1px solid ${QUILL_LINE}`, paddingTop: 12, marginTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'baseline' }}>
              <div style={{ color: QUILL_YELLOW, fontFamily: 'Georgia, serif', fontSize: 15 }}>
                Draft · rev {draft.revision}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {['tighten','humanise','heighten','simplify','broadcast_safe'].map((p) => (
                  <GhostBtn key={p} onClick={() => revise(p)} disabled={busy === 'revise'} testId={`quill-revise-${p}`}>
                    {p.replace('_', ' ')}
                  </GhostBtn>
                ))}
              </div>
            </div>
            <pre data-testid="quill-draft-text" style={{
              background: QUILL_ESPRESSO, padding: 18, border: `1px solid ${QUILL_LINE}`,
              color: QUILL_INK, fontFamily: 'Georgia, "Iowan Old Style", serif',
              fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              borderRadius: 2, margin: 0,
            }}>{draft.text}</pre>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <GhostBtn
                onClick={() => navigator.clipboard.writeText(draft.text)}
                testId="quill-draft-copy"
              >COPY TO CLIPBOARD</GhostBtn>
              {onJumpToScript && (
                <GhostBtn onClick={onJumpToScript} testId="quill-draft-to-script">SEND TO SCRIPT LAB</GhostBtn>
              )}
            </div>
            <div style={{ color: QUILL_MUTED, fontSize: 11, marginTop: 8, letterSpacing: '0.08em' }}>
              {draft.style_notes.join(' · ')}
            </div>
          </div>
        )}
      </Shelf>
    </div>
  );
}

// --------------- Script Lab view (talk-show pack) ----------------

function ScriptLabView() {
  const [blocks, setBlocks] = useState([]);
  useEffect(() => {
    api.get('/api/quill/talk-show-pack').then((r) => setBlocks(r.data.blocks || [])).catch(() => {});
  }, []);
  return (
    <div data-testid="quill-script-view">
      <Shelf title="Rundown Blocks · Talk-Show Pack" testId="quill-script-shelf">
        <div style={{ color: QUILL_MUTED, fontSize: 13, fontFamily: 'Georgia, serif', fontStyle: 'italic', marginBottom: 12 }}>
          Reusable segment skeletons Quill fills in for on-air blocks — cold
          opens, factual briefs, challenge rounds, interviews, hand-offs, guest
          intros, roundtable prompts, closing notes, teasers.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {blocks.map((b) => (
            <div key={b.block_id} data-testid={`quill-block-${b.kind}`} style={{
              background: QUILL_ESPRESSO, border: `1px solid ${QUILL_LINE}`,
              padding: '14px 16px', borderRadius: 3,
            }}>
              <div style={{ color: QUILL_YELLOW, fontFamily: 'Georgia, serif', fontSize: 14, letterSpacing: '0.04em', marginBottom: 8 }}>
                {b.kind.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </div>
              <pre style={{
                color: QUILL_INK, fontFamily: 'Georgia, serif', fontSize: 13,
                lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: 0,
              }}>{b.template}</pre>
              {(b.cues || []).length > 0 && (
                <ul style={{ color: QUILL_MUTED, fontSize: 11, paddingLeft: 16, marginTop: 8 }}>
                  {b.cues.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </Shelf>
    </div>
  );
}

function NewsroomProductionDeck({ meta }) {
  const wordCount = Number(meta?.wordCount || 0);
  const runtimeSeconds = Math.max(12, Math.round(wordCount / 2.4));
  const countdown = `${Math.floor(runtimeSeconds / 60)}:${String(runtimeSeconds % 60).padStart(2, '0')}`;
  const lowerThird = (meta?.topic || meta?.objective || 'Awaiting topic lock').slice(0, 72);
  const introQueue = [
    'Cold open',
    'Lead fact stack',
    'Panel challenge',
  ];
  const outroQueue = [
    'Next-watch cue',
    'Anchor handoff',
    'Close signature',
  ];
  const cueMarkers = Array.isArray(meta?.cues) ? meta.cues : [];
  const segmentCards = [
    { id: 'SEG-01', title: 'Intro', state: meta?.draftState || 'idle' },
    { id: 'SEG-02', title: 'Challenge', state: meta?.busy ? 'drafting' : 'queued' },
    { id: 'SEG-03', title: 'Close', state: meta?.revision > 0 ? 'revising' : 'queued' },
  ];

  return (
    <Shelf title="Control Room · Live Script Runtime" testId="quill-newsroom-deck">
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
          <div style={_chipCard}>
            <span style={_chipLabel}>ACTIVE DRAFT</span>
            <strong style={_chipValue}>{String(meta?.draftState || 'idle').toUpperCase()}</strong>
          </div>
          <div style={_chipCard}>
            <span style={_chipLabel}>WORD COUNT</span>
            <strong style={_chipValue}>{wordCount}</strong>
          </div>
          <div style={_chipCard}>
            <span style={_chipLabel}>REVISION</span>
            <strong style={_chipValue}>R{meta?.revision || 0}</strong>
          </div>
          <div style={_chipCard}>
            <span style={_chipLabel}>RUNTIME COUNTDOWN</span>
            <strong style={_chipValue}>{countdown}</strong>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 10 }}>
          <div style={_panelCard}>
            <div style={_panelTitle}>SEGMENT CARDS</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {segmentCards.map((card) => (
                <div key={card.id} style={_segmentCard}>
                  <div style={{ fontSize: 10, color: QUILL_MUTED }}>{card.id}</div>
                  <div style={{ fontSize: 13, color: QUILL_INK }}>{card.title}</div>
                  <div style={{ fontSize: 10, color: '#ff8f5a' }}>{String(card.state).toUpperCase()}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: meta?.busy ? '#fca5a5' : '#86efac' }}>
              {meta?.busy ? '● Live typing indicator: Quill drafting in real time' : '● Draft engine idle'}
            </div>
          </div>

          <div style={_panelCard}>
            <div style={_panelTitle}>LOWER-THIRD PREVIEW</div>
            <div style={{ background: 'rgba(255,122,69,0.08)', border: `1px solid ${QUILL_LINE}`, padding: '8px 10px', fontSize: 12 }}>
              {lowerThird}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: QUILL_MUTED }}>
              Anchor handoff: {meta?.hostVoice ? `${String(meta.hostVoice).toUpperCase()} takes lead` : 'pending host'}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={_panelCard}>
            <div style={_panelTitle}>INTRO/OUTRO QUEUE</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <div style={{ color: QUILL_MUTED, fontSize: 10, marginBottom: 4 }}>INTRO</div>
                {introQueue.map((item) => <div key={item} style={_queueRow}>• {item}</div>)}
              </div>
              <div>
                <div style={{ color: QUILL_MUTED, fontSize: 10, marginBottom: 4 }}>OUTRO</div>
                {outroQueue.map((item) => <div key={item} style={_queueRow}>• {item}</div>)}
              </div>
            </div>
          </div>
          <div style={_panelCard}>
            <div style={_panelTitle}>CUE MARKERS</div>
            {cueMarkers.length === 0 ? (
              <div style={{ color: QUILL_MUTED, fontSize: 11 }}>Awaiting outline beats.</div>
            ) : (
              cueMarkers.map((cue, idx) => (
                <div key={`${cue}-${idx}`} style={_queueRow}>
                  {idx + 1}. {cue}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Shelf>
  );
}

// --------------- Placeholder shelf views ----------------

function PlaceholderView({ view }) {
  return (
    <Shelf title={view.label} testId={`quill-${view.id}-view`}>
      <div style={{
        color: QUILL_MUTED, fontFamily: 'Georgia, serif', fontStyle: 'italic',
        fontSize: 14, lineHeight: 1.6,
      }}>
        {view.blurb}.
        <br/><br/>
        This panel is pending activation. Use <strong style={{ color: QUILL_YELLOW, fontStyle: 'normal' }}>Script Desk</strong> and <strong style={{ color: QUILL_YELLOW, fontStyle: 'normal' }}>Wire Desk</strong> for live production work.
      </div>
    </Shelf>
  );
}

// --------------- Main shell ----------------

const _label = { color: QUILL_MUTED, fontSize: 11, letterSpacing: '0.12em', display: 'block', marginBottom: 4 };
const _input = {
  width: '100%', background: QUILL_ESPRESSO, color: QUILL_INK,
  border: `1px solid ${QUILL_LINE}`, padding: '8px 10px', fontSize: 13,
  fontFamily: 'Georgia, serif', borderRadius: 2, outline: 'none',
};
const _chipCard = {
  background: 'rgba(13,20,33,0.95)',
  border: `1px solid ${QUILL_LINE}`,
  borderRadius: 4,
  padding: '8px 10px',
  display: 'grid',
  gap: 4,
};
const _chipLabel = { color: QUILL_MUTED, fontSize: 9, letterSpacing: '0.2em' };
const _chipValue = { color: QUILL_CREAM, fontSize: 14, letterSpacing: '0.04em' };
const _panelCard = {
  background: 'rgba(13,20,33,0.9)',
  border: `1px solid ${QUILL_LINE}`,
  borderRadius: 4,
  padding: '10px 12px',
};
const _panelTitle = { color: '#ff9f6a', fontSize: 10, letterSpacing: '0.22em', marginBottom: 6 };
const _segmentCard = {
  minWidth: 90,
  background: 'rgba(4,7,15,0.85)',
  border: `1px solid ${QUILL_LINE}`,
  borderRadius: 4,
  padding: '6px 8px',
  display: 'grid',
  gap: 2,
};
const _queueRow = { fontSize: 11, color: QUILL_INK, marginBottom: 4 };

export default function QuillStudio({ onNavigate }) {
  const [view, setView] = useState('library');
  const [productionMeta, setProductionMeta] = useState({
    mode: 'episode_opener',
    topic: '',
    objective: '',
    hostVoice: 'nova',
    busy: false,
    busyState: 'idle',
    revision: 0,
    wordCount: 0,
    draftState: 'idle',
    cues: [],
  });

  const active = VIEWS.find((v) => v.id === view) || VIEWS[0];

  const body = (() => {
    if (view === 'library') return <LibraryView />;
    if (view === 'desk')    return <DraftDeskView onJumpToScript={() => setView('script')} onDraftStateChange={setProductionMeta} />;
    if (view === 'script')  return <ScriptLabView />;
    return <PlaceholderView view={active} />;
  })();

  return (
    <div data-testid="quill-studio" style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${QUILL_ESPRESSO} 0%, ${QUILL_CHAR} 100%)`,
      color: QUILL_INK,
      fontFamily: '"Iowan Old Style", Georgia, "Times New Roman", serif',
      padding: '64px 56px 40px',
    }}>
      {/* Header strip */}
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        borderBottom: `1px solid ${QUILL_LINE}`, paddingBottom: 12, marginBottom: 18,
      }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: '0.42em', color: QUILL_YELLOW }}>
            SIGNAL · QUILL
          </div>
          <div style={{ fontSize: 28, marginTop: 4, color: QUILL_CREAM }}>
            Live newsroom script desk
          </div>
        </div>
      </header>

      <NewsroomProductionDeck meta={productionMeta} />

      {/* View nav */}
      <nav data-testid="quill-view-nav" style={{
        display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20,
      }}>
        {VIEWS.map((v) => (
          <GhostBtn
            key={v.id} onClick={() => setView(v.id)} active={view === v.id}
            testId={`quill-view-${v.id}`}
          >{v.label}</GhostBtn>
        ))}
      </nav>

      {body}
    </div>
  );
}
